import { StatusPill } from '@vnetwork/ui/data';
import {
  useVpnGateways,
  useIkePolicies,
  useIpsecPolicies,
  useS2sConnections,
  useCreateS2sConnection,
  useDeleteS2sConnection,
  useUpdateS2sConnection,
} from '@/api/opennebula';
import { GenericList, mutedCell } from './GenericList';

export function S2sTab({ serviceId }: { serviceId: string }) {
  // A site-to-site tunnel needs its gateway, both crypto policies and a
  // pre-shared key, so offer them as pickers populated from the live lists.
  const gateways = useVpnGateways(serviceId).data ?? [];
  const ikePolicies = useIkePolicies(serviceId).data ?? [];
  const ipsecPolicies = useIpsecPolicies(serviceId).data ?? [];
  const opts = <T extends { id: string; name?: string }>(rows: T[]) => rows.map((r) => ({ value: r.id, label: r.name || r.id }));
  return (
    <GenericList title="Site-to-site connections" list={useS2sConnections(serviceId)} create={useCreateS2sConnection(serviceId)} remove={useDeleteS2sConnection(serviceId)} update={useUpdateS2sConnection(serviceId)}
      fields={[
        { key: 'name', label: 'Name' },
        { key: 'vpn_gateway_id', label: 'VPN gateway', type: 'select', placeholder: 'Select a gateway', options: opts(gateways) },
        { key: 'ike_policy_id', label: 'IKE policy', type: 'select', placeholder: 'Select an IKE policy', options: opts(ikePolicies) },
        { key: 'ipsec_policy_id', label: 'IPSec policy', type: 'select', placeholder: 'Select an IPSec policy', options: opts(ipsecPolicies) },
        { key: 'remote_gateway_ip', label: 'Remote gateway IP', placeholder: '203.0.113.1' },
        { key: 'psk', label: 'Pre-shared key', placeholder: 'Shared secret' },
        { key: 'local_subnet_cidrs', label: 'Local subnets', placeholder: '10.0.0.0/24' },
        { key: 'remote_subnet_cidrs', label: 'Remote subnets', placeholder: '192.168.0.0/24' },
        { key: 'close_action', label: 'Close action', type: 'select', default: 'none', optional: true, options: [
          { value: 'none', label: 'None' }, { value: 'hold', label: 'Hold' }, { value: 'restart', label: 'Restart' },
        ] },
      ]}
      columns={[
        { id: 'name', header: 'Name', cell: ({ row }) => <b style={{ fontWeight: 500 }}>{row.original.name || row.original.id}</b> },
        { id: 'peer', header: 'Remote gateway', cell: ({ row }) => <span style={{ color: 'var(--vn-fg-muted)', fontVariantNumeric: 'tabular-nums' }}>{row.original.remote_gateway_ip || '—'}</span> },
        { id: 'local', header: 'Local subnets', cell: ({ row }) => mutedCell(row.original.local_subnet_cidrs) },
        { id: 'remote', header: 'Remote subnets', cell: ({ row }) => mutedCell(row.original.remote_subnet_cidrs) },
        { id: 'status', header: 'Status', cell: ({ row }) => <StatusPill tone={/up|active|connect/i.test(row.original.status ?? '') ? 'success' : 'muted'}>{row.original.status || 'Unknown'}</StatusPill> },
      ]}
    />
  );
}
